import {
  createRootRoute,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/router-devtools";
import { useEffect, useRef } from "react";

import {
  analyticsQueryString,
  isAnalyticsTrackedPath,
} from "@/lib/analyticsScope";
import { isPrerendering } from "@/lib/prerender";

const GA_ID = import.meta.env.VITE_GOOGLE_ANALYTICS_ID;

/**
 * Google Analytics, scoped to the public marketing site.
 *
 * WHERE IT IS ALLOWED TO LOOK. GA used to initialise app-wide, so every portal
 * page load sent its URL to Google - and portal URLs carry organisation,
 * table, record and workflow identifiers. The scope is now an allow-list of
 * marketing paths plus `/portal/sign-up`, the point where a marketing visit
 * becomes an account (analyticsScope.ts); anything not on it is invisible to
 * analytics, including `/auth/callback`, whose URL carries an access token and
 * the user's email.
 *
 * The allow-list covers query parameters as well as paths, because
 * page_location carries both - see analyticsQueryString.
 *
 * Two separate things have to be true for that to hold, and only one of them
 * is this guard. The other is that the tag must never be baked into the built
 * HTML - see the prerender check below, which is why it loaded on every portal
 * page in production despite an earlier version of this guard.
 *
 * The guard covers what we SEND as well as what we load, because gtag.js stays
 * loaded once initialised. It cannot, however, cover what gtag.js sends BY
 * ITSELF. GA4 Enhanced Measurement > Page views > advanced settings has "page
 * changes based on browser history events", ON by default, which fires a page
 * view on every pushState - so portal URLs would be reported despite this
 * guard, and tracked navigations counted twice. `send_page_view: false` below
 * suppresses only the initial hit, not those.
 *
 * That ONE sub-option has to be off in the GA4 property. Enhanced Measurement
 * itself should stay on: scrolls, outbound clicks, downloads, video and site
 * search are all wanted and none of them report a URL we have excluded.
 *
 * WHAT IT RECORDS. `gtag("config")` sends one page view per full page load and
 * nothing on client-side navigation, so a single-page site recorded only
 * landing pages - direct and organic arrivals appeared, the journey afterwards
 * did not. Every tracked navigation now sends one explicitly, including the
 * first, so there is exactly one code path and no dependence on when the async
 * script happens to land.
 */
function GoogleAnalytics() {
  // `resolvedLocation`, not `location`: the latter moves as soon as a
  // navigation starts, so reading it would report a page that may not render.
  const pathname = useRouterState({
    select: (state) =>
      state.resolvedLocation?.pathname ?? state.location.pathname,
  });
  const scriptLoaded = useRef(false);

  useEffect(() => {
    if (!GA_ID || import.meta.env.DEV) return;

    // Never under the prerenderer. This is not about hits - it is about the
    // TAG. The build runs in production mode with GA_ID set, so without this
    // the effect appended the gtag.js <script> to document.head, puppeteer
    // serialised it into dist/index.html, and the CI step that derives
    // dist/200.html from that file carried it into the SPA fallback - which
    // Azure serves for every portal URL and every 404. The script was
    // therefore loading on every signed-in page in production, entirely
    // outside the path guard below. It also meant each deploy fired a batch of
    // page views at the real property from the CI runner.
    if (isPrerendering()) return;

    if (!isAnalyticsTrackedPath(pathname)) return;

    if (!scriptLoaded.current) {
      scriptLoaded.current = true;

      const script = document.createElement("script");
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
      script.async = true;
      document.head.appendChild(script);

      // The command MUST be pushed as the `arguments` object - gtag.js only
      // recognises commands pushed this way ([object Arguments]). Pushing a
      // plain array (e.g. via rest params) is silently treated as a data-layer
      // variable write, so `config` never fires and no hits are sent. This
      // mirrors Google's canonical snippet.
      window.dataLayer = window.dataLayer || [];
      const gtag: (...args: unknown[]) => void = function () {
        // eslint-disable-next-line prefer-rest-params -- gtag.js requires the real `arguments` object
        window.dataLayer.push(arguments);
      };
      window.gtag = gtag;
      gtag("js", new Date());
      // No implicit page view: it is queued now and resolved whenever the async
      // script lands, reading the URL and title AT THAT MOMENT. A visitor who
      // clicks through first has the landing hit attributed to page two, which
      // is then also counted by the explicit send below.
      gtag("config", GA_ID, { send_page_view: false });
    }

    // Two frames, not one - but not for the reason it is tempting to write.
    // react-helmet-async commits inside its own requestAnimationFrame, and it
    // registers that frame during the RENDER phase, so on a client-side
    // navigation Helmet's frame is already queued before this effect runs and
    // one frame would do. The second frame is for the INITIAL load, where
    // `resolvedLocation` is undefined, no page Helmet has rendered yet, and
    // this effect does not re-run when it resolves to the same pathname.
    // Helmet also cancels and re-registers on every subsequent emit, so two
    // frames is a pragmatic margin rather than a guarantee.
    // Captured now, from the path the guard actually approved. Reading
    // window.location two frames later would report wherever the browser had
    // got to by then - which a navigation started in between could make an
    // untracked URL, gated on a tracked one.
    //
    // The query string is filtered rather than passed through: an invitation
    // reaches /portal/sign-up as ?inviteEmail=<address>&inviteOrg=<name>, and
    // page_location is what GA4 stores. Only campaign parameters survive.
    const approvedHref =
      window.location.origin +
      pathname +
      analyticsQueryString(window.location.search);

    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => {
        window.gtag?.("event", "page_view", {
          // GA4 derives the path from page_location; page_path is a Universal
          // Analytics parameter and is ignored, so it is not sent here.
          page_location: approvedHref,
          page_title: document.title,
          // Without this GA4 falls back to document.referrer, which does not
          // change on client-side navigation - so every in-app hit would carry
          // the original external referrer and the journey would not join up.
          page_referrer: document.referrer || undefined,
        });
      });
    });
    return () => {
      cancelAnimationFrame(first);
      if (second) cancelAnimationFrame(second);
    };
  }, [pathname]);

  return null;
}

// Extend Window interface for gtag
declare global {
  interface Window {
    dataLayer: unknown[];
    // Optional: absent for any session that never visits a tracked page.
    gtag?: (...args: unknown[]) => void;
  }
}

export const Route = createRootRoute({
  component: () => (
    <>
      {/*
        No Helmet here. charset and viewport are already declared in
        index.html, and Helmet cannot replace tags it does not manage - so
        emitting them again duplicated both on every prerendered page, with the
        second charset landing about a kilobyte into the head where it is
        invalid. It also made every page carry a data-rh tag regardless of
        whether its own head had rendered, which defeated the freshness check
        in the prerender verification.
      */}
      <GoogleAnalytics />
      <Outlet />
      {import.meta.env.DEV && <TanStackRouterDevtools />}
    </>
  ),
});
